import type { TerraDraw, TerraDrawExtend } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

type AdapterOptions = ConstructorParameters<typeof TerraDrawGoogleMapsAdapter>[0];
type RoofDragTarget = { kind: "vertex" | "midpoint"; lng: number; lat: number };

/** Polygon-mode coordinate points remain visible in select mode, before a
 * polygon is selected. Treat those visible handles as editable targets too. */
export function selectRoofVertexForDrag(draw: TerraDraw, event: PointerEvent, adapter: Pick<TerraDrawGoogleMapsAdapter, "project" | "getMapEventElement">): RoofDragTarget | false {
  if (draw.getMode() !== "select") return false;
  const rect = adapter.getMapEventElement().getBoundingClientRect();
  const rank = (properties: Record<string, unknown>) => properties.midPoint === true ? 3 : properties.selectionPoint === true ? 2 : 1;
  const points = draw.getFeaturesAtPointerEvent(event, {
    pointerDistance: 8, ignoreCoordinatePoints: false, ignoreSelectFeatures: false,
  }).filter(feature => feature.geometry.type === "Point" && (feature.properties.selectionPoint === true || feature.properties.midPoint === true || feature.properties.coordinatePoint === true))
    .map(feature => {
      const [lng, lat] = (feature.geometry as { coordinates: number[] }).coordinates;
      const p = adapter.project(lng, lat);
      return { feature, lng, lat, distance: Math.hypot(p.x - (event.clientX - rect.left), p.y - (event.clientY - rect.top)) };
    }).sort((a, b) => a.distance - b.distance || rank(b.feature.properties) - rank(a.feature.properties));
  // Resolve the nearest actual guidance geometry, not store iteration order or
  // Google canvas CSS/z-index. Selected handles win exact coordinate ties.
  const point = points[0];
  if (!point) return false;
  const properties = point.feature.properties;
  const featureId = properties.coordinatePoint === true ? properties.coordinatePointFeatureId
    : properties.selectionPoint === true ? properties.selectionPointFeatureId : properties.midPointFeatureId;
  if (typeof featureId !== "string") return false;
  const parent = draw.getSnapshotFeature(featureId);
  if (!parent) return false;
  if (properties.coordinatePoint === true && parent.properties.selected !== true) draw.selectFeature(featureId);
  return { kind: properties.midPoint === true ? "midpoint" : "vertex", lng: point.lng, lat: point.lat };
}

/**
 * The official adapter publicly exposes its current Google Maps event surface.
 * Keep that surface as a distinct, validated hit-test pane, but listen at the
 * map root so pointer-captured events from the underlying Data handles reach
 * Terra Draw. No Google private CSS selectors are used by this integration.
 */
export class OdsTerraDrawGoogleMapsAdapter extends TerraDrawGoogleMapsAdapter {
  private readonly eventRoot: HTMLElement;
  private readonly interactionPane: HTMLElement;

  private readonly prepareVertexDrag?: (event: PointerEvent) => RoofDragTarget | boolean;
  private vertexOrigin: ReturnType<OdsTerraDrawGoogleMapsAdapter["getDrawEventFromEvent"]> = null;
  private vertexPointer: number | null = null;
  private vertexKind: RoofDragTarget["kind"] | null = null;

  constructor(options: AdapterOptions & { eventRoot: HTMLElement; prepareVertexDrag?: (event: PointerEvent) => RoofDragTarget | boolean }) {
    const { eventRoot, prepareVertexDrag, ...adapterOptions } = options;
    super({ ...adapterOptions, forwardMapElementEvents: true });
    const pane = super.getMapEventElement();
    if (!(pane instanceof HTMLElement) || !eventRoot.contains(pane)) {
      throw new Error("Google Maps adapter event pane is unavailable inside the map viewport.");
    }
    const mapRect = eventRoot.getBoundingClientRect();
    const paneRect = pane.getBoundingClientRect();
    const aligned = Math.abs(mapRect.left - paneRect.left) <= 1
      && Math.abs(mapRect.top - paneRect.top) <= 1
      && Math.abs(mapRect.width - paneRect.width) <= 1
      && Math.abs(mapRect.height - paneRect.height) <= 1;
    const computed = getComputedStyle(pane);
    if (!aligned || computed.display === "none" || computed.visibility === "hidden") {
      throw new Error("Google Maps adapter event pane does not match the visible map viewport.");
    }
    this.prepareVertexDrag = prepareVertexDrag;
    this.eventRoot = eventRoot;
    this.interactionPane = pane;
  }

  private resetVertexGesture(pointerId: number | null, restoreMap = pointerId !== null): unknown[] {
    const failures: unknown[] = [];
    this.vertexOrigin = null;
    this.vertexKind = null;
    this.vertexPointer = null;
    this._dragState = "not-dragging";
    if (pointerId !== null) {
      try {
        if (this.eventRoot.hasPointerCapture(pointerId))
          this.eventRoot.releasePointerCapture(pointerId);
      } catch (error) { failures.push(error); }
    }
    if (restoreMap) {
      try { this.setDraggability(true); } catch (error) { failures.push(error); }
    }
    return failures;
  }

  private throwCleanupFailures(failures: unknown[]): void {
    if (!failures.length) return;
    if (failures.length > 1)
      throw new AggregateError(failures, "Pointer gesture cleanup failed.");
    throw failures[0];
  }

  private readonly beginVertex = (event: PointerEvent) => {
    if (!event.isPrimary || event.button !== 0) return;
    const target = event.target;
    if (target instanceof Element && target.closest("button,a,input,select,textarea,[role=button],[contenteditable=true]")) return;
    const origin = this.getDrawEventFromEvent(event);
    if (!origin) return;
    const targetHandle = this.prepareVertexDrag?.(event);
    if (!targetHandle) return;
    this.vertexKind = typeof targetHandle === "object" ? targetHandle.kind : "vertex";
    this.vertexOrigin = typeof targetHandle === "object"
      ? { ...origin, lng: targetHandle.lng, lat: targetHandle.lat, containerX: this.project(targetHandle.lng, targetHandle.lat).x, containerY: this.project(targetHandle.lng, targetHandle.lat).y }
      : origin;
    this.vertexPointer = event.pointerId;
    try {
      // Disable Google's gesture before it sees the down event, not after its
      // first move. Capture also survives selection replacing the handle canvas.
      this.eventRoot.setPointerCapture(event.pointerId);
      this.setDraggability(false);
    } catch (error) {
      // A partially acquired capture must not leave a stale vertex gesture or
      // prevent Google map gestures from being restored.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.throwCleanupFailures([error, ...this.resetVertexGesture(event.pointerId)]);
    }
  };

  private readonly endVertex = (event: PointerEvent) => {
    if (event.pointerId !== this.vertexPointer) return;
    const failures: unknown[] = [];
    try {
      if (event.type === "pointercancel") {
        const end = this.getDrawEventFromEvent(event) ?? this.vertexOrigin;
        if (end && this._dragState === "dragging")
          this._currentModeCallbacks?.onDragEnd(end, enabled => this.setDraggability(enabled));
      }
    } catch (error) {
      failures.push(error);
    } finally {
      failures.push(...this.resetVertexGesture(event.pointerId));
    }
    this.throwCleanupFailures(failures);
  };

  override register(callbacks: TerraDrawExtend.TerraDrawCallbacks): void {
    try {
      super.register({
        ...callbacks,
        onDragStart: (event, setDraggability) => {
          if (!this.vertexOrigin) { callbacks.onDragStart(event, setDraggability); return; }
          // Upstream starts at the FIRST MOVE and consumes that move without
          // onDrag. A fast/coalesced drag can already be outside the hit radius,
          // or can end without a second move. Anchor at down; consume this move.
          // SelectMode drag-start checks a nearby coordinate before its chosen
          // midpoint. Its public click path correctly inserts the nearest
          // midpoint first; then start a normal coordinate drag at that point.
          if (this.vertexKind === "midpoint") callbacks.onClick(this.vertexOrigin);
          callbacks.onMouseMove(this.vertexOrigin);
          callbacks.onDragStart(this.vertexOrigin, setDraggability);
          callbacks.onDrag(event, setDraggability);
        },
      });
      this.eventRoot.addEventListener("pointerdown", this.beginVertex, true);
      this.eventRoot.addEventListener("pointerup", this.endVertex);
      this.eventRoot.addEventListener("pointercancel", this.endVertex);
    } catch (error) {
      const failures: unknown[] = [error];
      for (const [type, listener, capture] of [
        ["pointerdown", this.beginVertex, true],
        ["pointerup", this.endVertex, false],
        ["pointercancel", this.endVertex, false],
      ] as const) {
        try { this.eventRoot.removeEventListener(type, listener, capture); } catch (cleanupError) { failures.push(cleanupError); }
      }
      failures.push(...this.resetVertexGesture(this.vertexPointer, true));
      try { super.unregister(); } catch (cleanupError) { failures.push(cleanupError); }
      this.throwCleanupFailures(failures);
    }
  }

  override unregister(): void {
    const failures: unknown[] = [];
    for (const [type, listener, capture] of [
      ["pointerdown", this.beginVertex, true],
      ["pointerup", this.endVertex, false],
      ["pointercancel", this.endVertex, false],
    ] as const) {
      try { this.eventRoot.removeEventListener(type, listener, capture); } catch (error) { failures.push(error); }
    }
    failures.push(...this.resetVertexGesture(this.vertexPointer, true));
    try { super.unregister(); } catch (error) { failures.push(error); }
    this.throwCleanupFailures(failures);
  }

  getInteractionPane(): HTMLElement {
    return this.interactionPane;
  }

  override getMapEventElement(_eventType?: Parameters<TerraDrawGoogleMapsAdapter["getMapEventElement"]>[0]): HTMLElement {
    return this.eventRoot;
  }
}
